import { LocalVideoChamada } from '_metronic/helpers/enumsUI'
import { VideoAtendimentoAgoraProps } from '../../atendimento-video/types'

/**
 * Só dados: os callbacks do player pertencem ao provider (estáveis), nunca à tela que iniciou a chamada.
 * `protocolo` e `sexoIdade` não vão para o player — só identificam o atendimento no cabeçalho da janela PiP.
 */
export type DadosChamada = Pick<
  VideoAtendimentoAgoraProps,
  'idAtendimento' | 'idPessoa' | 'isPaciente' | 'pacienteNome'
> & {
  protocolo?: string
  /** Texto pronto do cabeçalho da tela do atendimento (ex.: "Feminino · 34 anos"), exibido igual no PiP. */
  sexoIdade?: string
}

export interface ChamadaAtivaContextValue {
  /** Atendimento com chamada ativa nesta aba, ou `null`. */
  atendimentoId: number | null
  localDoVideo: LocalVideoChamada
  /** Document PiP disponível neste navegador (Chrome/Edge). */
  pipSuportado: boolean
  participantes: number
  chatAberto: boolean
  setChatAberto: (aberto: boolean) => void
  /** Idempotente: sem efeito se já houver chamada ativa nesta aba (deste ou de outro atendimento). */
  iniciarChamada: (dados: DadosChamada) => void
  /** No-op quando o id não é o da chamada ativa. */
  encerrarChamada: (atendimentoId: number) => void
  /** Deve ser chamado num clique (usa o gesto do usuário). */
  minimizar: () => void
  /** Traz o vídeo do PiP de volta para a aba (caminho por teclado, sem depender do X da janela). */
  restaurar: () => void
  /** Callback ref estável do encaixe na tela do atendimento. */
  registrarEncaixe: (el: HTMLElement | null) => void
}

export type SessaoAtendimento = {
  rascunhoAnamnese: string
  anamneseSalva: string
  observacoesExibidas: boolean
}

/** O que a tela do atendimento lê e escreve, nos dois modos (sessão compartilhada ou estado local). */
export type SessaoDaTela = SessaoAtendimento & {
  atualizar: (parcial: Partial<SessaoAtendimento>) => void
}

export const SESSAO_INICIAL: SessaoAtendimento = {
  rascunhoAnamnese: '',
  anamneseSalva: '',
  observacoesExibidas: false,
}

export interface SessaoAtendimentoContextValue {
  /** A sessão compartilhada só vale para o atendimento da chamada ativa (ou para qualquer um, sem chamada). */
  podeUsarSessao: (atendimentoId: number) => boolean
  obterSessao: (atendimentoId: number) => SessaoAtendimento | null
  atualizarSessao: (atendimentoId: number, parcial: Partial<SessaoAtendimento>) => void
}

/** Altura fixa do cabeçalho da janela PiP (classe `h-60px`): entra na conta do tamanho da janela. */
export const ALTURA_CABECALHO_PIP = 60
/** A área do vídeo cabe em 480×480; abaixo de 360 de largura o cabeçalho não cabe. */
const LADO_MAXIMO_VIDEO_PIP = 480
const LARGURA_MINIMA_PIP = 360
const PROPORCAO_PADRAO = 16 / 9

/**
 * Tamanho pedido para a janela PiP: a área do vídeo na proporção da câmera do paciente — o player usa
 * `object-fit: contain` (não corta a imagem, contexto clínico), então qualquer diferença de proporção vira
 * faixa preta. Câmera em pé limita a altura e, abaixo da largura mínima, aceita faixas laterais. O Chrome
 * respeita o pedido (spike), mas guarda o tamanho se o médico redimensionar a janela.
 */
export const dimensoesJanelaPip = (proporcao?: number) => {
  const p = proporcao && Number.isFinite(proporcao) && proporcao > 0 ? proporcao : PROPORCAO_PADRAO
  const deitada = p >= 1
  const largura = deitada
    ? LADO_MAXIMO_VIDEO_PIP
    : Math.max(LARGURA_MINIMA_PIP, Math.round(LADO_MAXIMO_VIDEO_PIP * p))
  const alturaVideo = deitada ? Math.round(LADO_MAXIMO_VIDEO_PIP / p) : LADO_MAXIMO_VIDEO_PIP
  return { largura, altura: alturaVideo + ALTURA_CABECALHO_PIP }
}

export const ID_MINI_PLAYER = 'chamada-ativa-mini-player'

export const rotaDaChamada = (atendimentoId: number) =>
  `/apps/atendimentosMedicos/teleAtendimento?atendimento=${atendimentoId}`

/** A chamada persistente só existe com o player Agora (o iframe legado segue como está). */
export const videoAgoraHabilitado = () => process.env.REACT_APP_VIDEO_APP === 'AGORA'
